import { apiClient } from "./client";
import type { VesselId } from "../types/vessel";
import type {
  BudgetAllocationCreatePayload,
  BudgetAllocationListResponse,
  BudgetAuthorityConfigEntry,
  BudgetAllocationSummary,
  BudgetAllocationsQueryParams,
  BudgetAllocationUpdatePayload,
  BudgetCodeExclusionCreatePayload,
  BudgetCodeExclusionSummary,
  BudgetFleetSummaryRow,
  BudgetLineItem,
  BudgetLineItemSavePayload,
  BudgetPlanCreatePayload,
  BudgetPlanListResponse,
  BudgetPlanQueryParams,
  BudgetPlanRevisePayload,
  BudgetPlanSummary,
  BudgetPlanSummaryDetail,
  BudgetPlanUpdatePayload,
  BudgetRevisionApprovePayload,
  BudgetRevisionCreatePayload,
  BudgetRevisionRejectPayload,
  BudgetRevisionSummary,
  BudgetRevisionsQueryParams,
  RecoveryEntryCreatePayload,
  RecoveryEntriesQueryParams,
  RecoveryEntrySummary,
  BudgetStatus,
  BudgetTrackingSummary,
  BudgetUtilizationQueryParams,
  BudgetUtilizationSummary,
  BudgetWorksheetRow,
  BudgetWorksheetSavePayload,
  DrydockAmortisationRow,
  BudgetYearCreatePayload,
  BudgetYearStatusPayload,
  BudgetYearsQueryParams,
  BudgetYearSummary,
} from "../types/budget";

interface DataEnvelope<TData> {
  data: TData;
}

function buildBudgetPlanSearchParams(params: BudgetPlanQueryParams = {}) {
  const searchParams = new URLSearchParams();
  if (params.page !== undefined) {
    searchParams.set("page", String(params.page));
  }
  if (params.page_size !== undefined) {
    searchParams.set("page_size", String(params.page_size));
  }
  if (params.vessel_id !== undefined) {
    searchParams.set("vessel_id", String(params.vessel_id));
  }
  if (params.year_label !== undefined) {
    searchParams.set("year_label", String(params.year_label));
  }
  if (params.budget_type) {
    searchParams.set("budget_type", params.budget_type);
  }
  if (params.status) {
    searchParams.set("status", params.status);
  }
  if (params.filter) {
    searchParams.set("filter", params.filter);
  }
  return searchParams;
}

export async function listBudgetYears(params?: BudgetYearsQueryParams) {
  const response = await apiClient.get<DataEnvelope<BudgetYearSummary[]>>("/purchase/budgets/years/", {
    params,
  });
  return response.data.data;
}

export async function listBudgetAllocations(params?: BudgetAllocationsQueryParams) {
  const response = await apiClient.get<BudgetAllocationListResponse>(
    "/purchase/budgets/allocations/",
    { params },
  );
  return response.data;
}

export async function listBudgetUtilization(params?: BudgetUtilizationQueryParams) {
  const response = await apiClient.get<DataEnvelope<BudgetUtilizationSummary[]>>(
    "/purchase/budgets/utilization/",
    { params },
  );
  return response.data.data;
}

export async function listBudgetRevisions(params?: BudgetRevisionsQueryParams) {
  const response = await apiClient.get<DataEnvelope<BudgetRevisionSummary[]>>(
    "/purchase/budgets/revisions/",
    { params },
  );
  return response.data.data;
}

export async function createBudgetYear(vesselId: VesselId, payload: BudgetYearCreatePayload) {
  const response = await apiClient.post<DataEnvelope<BudgetYearSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/`,
    payload,
  );
  return response.data.data;
}

export async function createBudgetAllocation(
  vesselId: VesselId,
  budgetYearId: string,
  payload: BudgetAllocationCreatePayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetAllocationSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/allocations/`,
    payload,
  );
  return response.data.data;
}

export async function updateBudgetAllocation(
  vesselId: VesselId,
  budgetYearId: string,
  allocationId: string,
  payload: BudgetAllocationUpdatePayload,
) {
  const response = await apiClient.patch<DataEnvelope<BudgetAllocationSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/allocations/${allocationId}/`,
    payload,
  );
  return response.data.data;
}

export async function requestBudgetRevision(
  vesselId: VesselId,
  budgetYearId: string,
  allocationId: string,
  payload: BudgetRevisionCreatePayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetRevisionSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/allocations/${allocationId}/revisions/`,
    payload,
  );
  return response.data.data;
}

export async function approveBudgetRevision(
  vesselId: VesselId,
  budgetYearId: string,
  revisionId: string,
  payload: BudgetRevisionApprovePayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetRevisionSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/revisions/${revisionId}/approve/`,
    payload,
  );
  return response.data.data;
}

export async function rejectBudgetRevision(
  vesselId: VesselId,
  budgetYearId: string,
  revisionId: string,
  payload: BudgetRevisionRejectPayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetRevisionSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/revisions/${revisionId}/reject/`,
    payload,
  );
  return response.data.data;
}

export async function closeBudgetYear(
  vesselId: VesselId,
  budgetYearId: string,
  payload: BudgetYearStatusPayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetYearSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/close/`,
    payload,
  );
  return response.data.data;
}

export async function reopenBudgetYear(
  vesselId: VesselId,
  budgetYearId: string,
  payload: BudgetYearStatusPayload,
) {
  const response = await apiClient.post<DataEnvelope<BudgetYearSummary>>(
    `/purchase/vessels/${vesselId}/budgets/years/${budgetYearId}/reopen/`,
    payload,
  );
  return response.data.data;
}

export async function listBudgetPlans(params: BudgetPlanQueryParams = {}) {
  const response = await apiClient.get<BudgetPlanListResponse>("/purchase/budgets/", {
    params: buildBudgetPlanSearchParams(params),
  });
  return response.data;
}

export async function getBudgetPlan(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/`);
  return response.data.data;
}

export async function createBudgetPlan(payload: BudgetPlanCreatePayload) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>("/purchase/budgets/", payload);
  return response.data.data;
}

export async function reviseBudgetPlan(sourceBudgetId: string, payload: BudgetPlanRevisePayload) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(
    `/purchase/budgets/${sourceBudgetId}/revise/`,
    payload,
  );
  return response.data.data;
}

export async function listBudgetAuthorityConfig() {
  const response = await apiClient.get<DataEnvelope<BudgetAuthorityConfigEntry[]>>("/purchase/budgets/authority/");
  return response.data.data;
}

export async function updateBudgetPlan(budgetId: string, payload: BudgetPlanUpdatePayload) {
  const response = await apiClient.patch<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/`, payload);
  return response.data.data;
}

export async function submitBudgetPlan(budgetId: string) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/submit/`, {});
  return response.data.data;
}

export async function approveBudgetPlan(budgetId: string) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/approve/`, {});
  return response.data.data;
}

export async function rejectBudgetPlan(budgetId: string, reason: string) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/reject/`, {
    reason,
  });
  return response.data.data;
}

export async function activateBudgetPlan(budgetId: string) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/activate/`, {});
  return response.data.data;
}

export async function closeBudgetPlan(budgetId: string, payload: BudgetYearStatusPayload) {
  const response = await apiClient.post<DataEnvelope<BudgetPlanSummary>>(`/purchase/budgets/${budgetId}/close/`, payload);
  return response.data.data;
}

export async function listBudgetWorksheetRows(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetWorksheetRow[]>>(`/purchase/budgets/${budgetId}/allocations/`);
  return response.data.data;
}

export async function saveBudgetWorksheet(budgetId: string, payload: BudgetWorksheetSavePayload) {
  const response = await apiClient.put<DataEnvelope<BudgetWorksheetRow[]>>(`/purchase/budgets/${budgetId}/allocations/`, payload);
  return response.data.data;
}

export async function listBudgetLineItems(budgetId: string, allocationId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetLineItem[]>>(
    `/purchase/budgets/${budgetId}/allocations/${allocationId}/line-items/`,
  );
  return response.data.data;
}

export async function saveBudgetLineItems(
  budgetId: string,
  allocationId: string,
  payload: BudgetLineItemSavePayload,
) {
  const response = await apiClient.put<DataEnvelope<BudgetLineItem[]>>(
    `/purchase/budgets/${budgetId}/allocations/${allocationId}/line-items/`,
    payload,
  );
  return response.data.data;
}

export async function listBudgetAmortisation(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<DrydockAmortisationRow[]>>(
    `/purchase/budgets/${budgetId}/amortisation/`,
  );
  return response.data.data;
}

export async function listBudgetExclusions(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetCodeExclusionSummary[]>>(`/purchase/budgets/${budgetId}/exclusions/`);
  return response.data.data;
}

export async function createBudgetExclusion(budgetId: string, payload: BudgetCodeExclusionCreatePayload) {
  const response = await apiClient.post<DataEnvelope<BudgetCodeExclusionSummary>>(`/purchase/budgets/${budgetId}/exclusions/`, payload);
  return response.data.data;
}

export async function deleteBudgetExclusion(budgetId: string, exclusionId: string) {
  await apiClient.delete(`/purchase/budgets/${budgetId}/exclusions/${exclusionId}/`);
}

export async function getBudgetPlanSummary(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetPlanSummaryDetail>>(`/purchase/budgets/${budgetId}/summary/`);
  return response.data.data;
}

export async function listMyBudgetApprovals() {
  const response = await apiClient.get<DataEnvelope<BudgetPlanSummary[]>>("/purchase/budgets/my-approvals/");
  return response.data.data;
}

export async function getBudgetPlanTracking(budgetId: string) {
  const response = await apiClient.get<DataEnvelope<BudgetTrackingSummary>>(`/purchase/budgets/${budgetId}/tracking/`);
  return response.data.data;
}

export async function listBudgetFleetSummary(params: { year_label?: number; status?: BudgetStatus } = {}) {
  const searchParams = new URLSearchParams();
  if (params.year_label !== undefined) {
    searchParams.set("year_label", String(params.year_label));
  }
  if (params.status) {
    searchParams.set("status", params.status);
  }
  const response = await apiClient.get<DataEnvelope<BudgetFleetSummaryRow[]>>("/purchase/budget-fleet-summary/", {
    params: searchParams,
  });
  return response.data.data;
}

export async function listRecoveryEntries(params: RecoveryEntriesQueryParams = {}) {
  const response = await apiClient.get<DataEnvelope<RecoveryEntrySummary[]>>("/purchase/recovery-entries/", {
    params,
  });
  return response.data.data;
}

export async function createRecoveryEntry(payload: RecoveryEntryCreatePayload) {
  const response = await apiClient.post<DataEnvelope<RecoveryEntrySummary>>("/purchase/recovery-entries/", payload);
  return response.data.data;
}

export async function verifyRecoveryEntry(recoveryEntryId: string) {
  const response = await apiClient.post<DataEnvelope<RecoveryEntrySummary>>(
    `/purchase/recovery-entries/${recoveryEntryId}/verify/`,
    {},
  );
  return response.data.data;
}
